import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import DesktopWindowsOutlinedIcon from '@mui/icons-material/DesktopWindowsOutlined';
import IosShareRoundedIcon from '@mui/icons-material/IosShareRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { ClayButton } from '@shared/components/ClayButton';
import { useNotify } from '@shared/notifications';
import { tokens } from '@theme/tokens';
import { useTranslation } from 'react-i18next';

export interface DesktopOnlyScreenProps {
  /** The page that was opened, e.g. "Projekte"; shown above the headline. */
  pageTitle?: string | undefined;
  /** Where a phone can go instead; given only to users who may open the price catalog. */
  onOpenCatalog?: (() => void) | undefined;
}

/**
 * What a phone shows in place of a page built for the desktop: the page's name, that it needs a
 * larger screen, the one way forward on this device (the price catalog), and a quiet way to take
 * the link to a computer — the share sheet where the phone has one, else the clipboard.
 */
export function DesktopOnlyScreen({ pageTitle, onOpenCatalog }: DesktopOnlyScreenProps) {
  const { t } = useTranslation();
  const notify = useNotify();
  const canShare = 'share' in navigator;

  const passLinkOn = async () => {
    const url = window.location.href;
    if (canShare) {
      try {
        await navigator.share({ url, title: document.title });
      } catch {
        // Closing the share sheet is a choice, not an error.
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      notify.success(t('desktopOnly.copied'));
    } catch {
      notify.error(t('desktopOnly.copyFailed'));
    }
  };

  return (
    <Stack
      sx={{
        // Fill the screen under the floating menu key, content centred like a quiet empty state.
        minHeight: 'calc(100dvh - 120px)',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: 1.25,
        px: 1,
        py: 4,
      }}
    >
      {/* The same clay key as the catalog's category cards. */}
      <Box
        aria-hidden
        sx={{
          width: 56,
          height: 56,
          mb: 1,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          color: '#ffffff',
          background: tokens.gradient.navPill,
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.25), 0 1px 3px rgba(15,38,38,0.25)',
        }}
      >
        <DesktopWindowsOutlinedIcon sx={{ fontSize: 28 }} />
      </Box>
      {pageTitle && (
        <Typography variant="overline" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
          {pageTitle}
        </Typography>
      )}
      <Typography variant="h5" component="h1">
        {t('desktopOnly.title')}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 360 }}>
        {onOpenCatalog ? t('desktopOnly.descriptionCatalog') : t('desktopOnly.description')}
      </Typography>
      <Stack sx={{ mt: 1.5, gap: 1, alignItems: 'center' }}>
        {onOpenCatalog && (
          <ClayButton onClick={onOpenCatalog} endIcon={<ArrowForwardRoundedIcon />}>
            {t('desktopOnly.toCatalog')}
          </ClayButton>
        )}
        <Button
          color="inherit"
          startIcon={canShare ? <IosShareRoundedIcon /> : <LinkRoundedIcon />}
          onClick={() => {
            void passLinkOn();
          }}
          sx={{ color: 'text.secondary' }}
        >
          {canShare ? t('desktopOnly.shareLink') : t('desktopOnly.copyLink')}
        </Button>
      </Stack>
    </Stack>
  );
}
